import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Waves, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050A15]">
      <div className="hidden lg:block relative qs-noise">
        <div className="absolute inset-0 grid place-items-center p-16">
          <div>
            <h2 className="font-display font-extrabold text-4xl qs-grad-text tracking-tighter">Enter the workspace.</h2>
            <p className="text-slate-400 mt-4 max-w-sm leading-relaxed">Analyze hyperspectral scenes, run structural simulations and mint on-chain certificates — all from one console.</p>
          </div>
        </div>
      </div>
      <div className="grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <span className="grid place-items-center h-9 w-9 rounded-sm bg-gradient-to-br from-[#8A2BE2] via-[#00FFFF] to-[#00FF7F]">
              <Waves className="h-5 w-5 text-[#050A15]" strokeWidth={2.5} />
            </span>
            <span className="font-display font-bold text-lg">Quantum<span className="qs-grad-text">Spectra</span></span>
          </Link>
          <h1 className="font-display font-bold text-2xl">Sign in</h1>
          <p className="text-slate-400 text-sm mt-1 mb-6">Access your custodial wallet & projects.</p>

          {err && <div data-testid="login-error" className="mb-4 text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-4 py-2.5">{err}</div>}

          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#0A1128] border border-white/10 px-4 py-3 mb-4 outline-none focus:border-[#00FFFF] transition-colors" />
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Password</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#0A1128] border border-white/10 px-4 py-3 mb-6 outline-none focus:border-[#00FFFF] transition-colors" />

          <button data-testid="login-submit" disabled={loading}
            className="qs-cta w-full flex items-center justify-center gap-2 bg-[#00FFFF] text-[#050A15] font-semibold py-3 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4" /></>}
          </button>
          <p className="text-sm text-slate-400 mt-5 text-center">No account? <Link to="/register" className="text-[#00FFFF] hover:underline">Create one</Link></p>
        </form>
      </div>
    </div>
  );
}
